// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { employeeKRAsUrl, acceptKRAUrl, submitSelfReviewUrl, saveSelfReviewDraftUrl } from "../../../Service/Apis";
import { getStatusChip, formatDate, calculateWeightedRating, getPeriodShortLabel, getPeriodDaysWarningColor, formatRatingLabel } from "../utils";
import RatingInput from "./components/RatingInput";
import { AssignedKRA } from "./Types/EmployeeKRATypes";
import { AxiosErrorResponse } from "./Types/ErrorTypes";
import SkillDocumentCommentBox from "../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";
import { isRichTextEmpty } from "../../../components/SkillDocumentCommentBox/utils";

const EmployeeKRAView: React.FC = () => {
  // 1. useState declarations
  const [assignedKRAs, setAssignedKRAs] = useState<AssignedKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState<boolean>(false);
  const [selfReviewDialogOpen, setSelfReviewDialogOpen] = useState<boolean>(false);
  const [selectedKRA, setSelectedKRA] = useState<AssignedKRA | null>(null);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [submittingSelfReview, setSubmittingSelfReview] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [showCommentsError, setShowCommentsError] = useState<boolean>(false);
  const [showKPICommentsError, setShowKPICommentsError] = useState<boolean>(false);

  // 2. const variables (hooks, Redux, computed constants)
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();
  const showToast = useCustomToast();

  // 3. Function declarations

  const fetchAssignedKRAs = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData(employeeKRAsUrl));
      
      if (response && Array.isArray(response)) {
        setAssignedKRAs(response);
      } else {
        setAssignedKRAs([]);
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load assigned KRAs";
      showToast(errorMessage, "error", true);
      setAssignedKRAs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptClick = (kra: AssignedKRA): void => {
    setSelectedKRA(kra);
    setAcceptDialogOpen(true);
  };

  const handleAcceptKRA = async (): Promise<void> => {
    if (!selectedKRA) return;

    // Prevent double submission
    if (accepting) return;

    setAccepting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        acceptKRAUrl(selectedKRA.employee_kra_id),
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.success) {
        // Update local state
        setAssignedKRAs((prev) =>
          prev.map((kra) =>
            kra.employee_kra_id === selectedKRA.employee_kra_id 
              ? { ...kra, status: "accepted", accepted_date: response.data.accepted_date } 
              : kra
          )
        );

        showToast("KRA accepted successfully!", "success", true);
        setAcceptDialogOpen(false);
        setSelectedKRA(null);
      } else {
        throw new Error(response.data?.message || "Failed to accept KRA");
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to accept KRA. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setAccepting(false);
    }
  };

  const handleSelfReviewClick = (kra: AssignedKRA): void => {
    // Deep copy to avoid state mutation
    setSelectedKRA(JSON.parse(JSON.stringify(kra)));
    setSelfReviewDialogOpen(true);
  };

  const handleSelfRatingChange = (
    sectionIndex: number,
    questionIndex: number,
    rating: number
  ): void => {
    if (!selectedKRA) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedKRA)) as AssignedKRA;
    updated.sections[sectionIndex].questions[questionIndex].self_rating = rating;
    setSelectedKRA(updated);
  };

  const handleKPICommentChange = (
    sectionIndex: number,
    questionIndex: number,
    comment: string
  ): void => {
    if (!selectedKRA) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedKRA)) as AssignedKRA;
    updated.sections[sectionIndex].questions[questionIndex].self_comments = comment;
    setSelectedKRA(updated);
  };

  const handleOverallCommentsChange = (comments: string): void => {
    if (!selectedKRA) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedKRA)) as AssignedKRA;
    updated.self_review_overall_comments = comments;
    setSelectedKRA(updated);
    // Clear error when user starts typing
    if (showCommentsError && comments.trim() !== "") {
      setShowCommentsError(false);
    }
  };

  const calculateSelfReviewOverallRating = (): number => {
    if (!selectedKRA) return 0;
    return calculateWeightedRating(selectedKRA.sections, 'self_rating');
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!selectedKRA) return;

    if (savingDraft) return; // Prevent double save

    setSavingDraft(true);
    try {
      const sectionsData = selectedKRA.sections.map((section) => ({
        section_id: section.section_id,
        questions: section.questions.map((q) => ({
          kpi_id: q.kpi_id,
          self_rating: q.self_rating,
          self_comments: q.self_comments || "",
        })),
      }));

      const payload = {
        sections: sectionsData,
        self_review_overall_rating: calculateSelfReviewOverallRating(),
        self_review_overall_comments: selectedKRA.self_review_overall_comments || "",
      };

      await axios.post(
        saveSelfReviewDraftUrl(selectedKRA.employee_kra_id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      showToast("Draft saved successfully! Refreshing data...", "success", true);
      
      // Refresh the data to show saved draft
      await fetchAssignedKRAs();
      
      // Close dialog to show updated data
      setSelfReviewDialogOpen(false);
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || "Failed to save draft";
      showToast(errorMessage, "error", true);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitSelfReview = async (): Promise<void> => {
    if (!selectedKRA) return;

    // Prevent double submission
    if (submittingSelfReview) return;

    // Validation: Check if all KPIs are rated (null/undefined check only, 0 is valid)
    let allRated = true;
    let unratedKPIs: string[] = [];
    
    selectedKRA.sections.forEach((section) => {
      section.questions.forEach((kpi) => {
        if (kpi.self_rating === null || kpi.self_rating === undefined) {
          allRated = false;
          unratedKPIs.push(kpi.question.substring(0, 50) + "...");
        }
      });
    });

    if (!allRated) {
      showToast(
        `Please rate all KPIs before submitting. Missing ${unratedKPIs.length} rating(s).`,
        "error",
        true
      );
      return;
    }

    // Validation: Check if all individual KPI comments are provided
    let allKPICommented = true;
    let uncommentedKPIs: string[] = [];

    selectedKRA.sections.forEach((section) => {
      section.questions.forEach((kpi) => {
        if (!kpi.self_comments || kpi.self_comments.trim() === "") {
          allKPICommented = false;
          uncommentedKPIs.push(kpi.question.substring(0, 50) + "...");
        }
      });
    });

    if (!allKPICommented) {
      setShowKPICommentsError(true);
      showToast(
        `Please add comments for all KPIs before submitting. Missing ${uncommentedKPIs.length} comment(s).`,
        "error",
        true
      );
      return;
    }
    setShowKPICommentsError(false);

    // Validation: Check if overall comments are provided
    if (isRichTextEmpty(selectedKRA.self_review_overall_comments)) {
      setShowCommentsError(true);
      showToast(
        "Please provide overall comments before submitting your review.",
        "error",
        true
      );
      return;
    }
    setShowCommentsError(false);

    const overallRating = calculateSelfReviewOverallRating();
    // Round to 2 decimal places to match backend DecimalField(max_digits=3, decimal_places=2)
    const roundedRating = Math.round(overallRating * 100) / 100;

    setSubmittingSelfReview(true);
    try {
      // Format data for backend API
      const sectionsData = selectedKRA.sections.map((section) => ({
        section_id: section.section_id,
        questions: section.questions.map((kpi) => ({
          kpi_id: kpi.kpi_id,
          self_rating: kpi.self_rating || 0,
          self_comments: kpi.self_comments || "",
        })),
      }));

      const payload = {
        sections: sectionsData,
        self_review_overall_rating: roundedRating,
        self_review_overall_comments: selectedKRA.self_review_overall_comments || "",
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        submitSelfReviewUrl(selectedKRA.employee_kra_id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.success) {
        // Update local state with response data
        setAssignedKRAs((prev) =>
          prev.map((kra) =>
            kra.employee_kra_id === selectedKRA.employee_kra_id
              ? response.data.data
              : kra
          )
        );

        showToast("Self-review submitted successfully!", "success", true);
        setSelfReviewDialogOpen(false);
        setSelectedKRA(null);
      } else {
        throw new Error(response.data?.message || "Failed to submit self-review");
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to submit self-review. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmittingSelfReview(false);
    }
  };

  // 4. useEffect hooks (just above return)
  useEffect(() => {
    fetchAssignedKRAs();
  }, []);


  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <PageTitle>My KRAs</PageTitle>
        </Box>
        <Typography variant="body1" color="black">
          View and manage your assigned Key Responsibility Areas and Key Performance Indicators
        </Typography>
      </Box>

      {/* Empty State */}
      {assignedKRAs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <AssignmentTurnedInIcon
            sx={{ fontSize: 80, color: "black", mb: 2 }}
          />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No KRAs Assigned Yet
          </Typography>
          <Typography variant="body2" color="black">
            You don't have any KRAs assigned at the moment. Check back later or contact your manager.
          </Typography>
        </Paper>
      ) : (
        /* KRA Cards */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {assignedKRAs.map((kra) => (
            <Card key={kra.employee_kra_id} elevation={3}>
              <CardContent sx={{ p: 3 }}>
                {/* KRA Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "600", mb: 1 }}>
                      {kra.form_name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                      <Typography variant="body2" color="black">
                        <strong>Designation:</strong> {kra.designation}
                      </Typography>
                      <Typography variant="body2" color="black">
                        <strong>Assigned:</strong> {formatDate(kra.assigned_date)}
                      </Typography>
                      {kra.assigned_by_name && (
                        <Typography variant="body2" color="black">
                          <strong>By:</strong> {kra.assigned_by_name}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                      <Typography variant="body2" color="black">
                        <strong>Period:</strong> {getPeriodShortLabel(kra.review_period)}
                      </Typography>
                      <Typography variant="body2" color="black">
                        <strong>End Date:</strong> {formatDate(kra.period_end_date)}
                      </Typography>
                      {kra.days_remaining > 0 && !kra.is_period_expired && (
                        <Chip 
                          label={`${kra.days_remaining} days remaining`}
                          size="small"
                          color={getPeriodDaysWarningColor(kra.days_remaining)}
                        />
                      )}
                      {kra.is_period_expired && (
                        <Chip label="Period Expired" size="small" color="error" />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end" }}>
                    {getStatusChip(kra.status)}
                    {kra.self_review_overall_rating != null && typeof kra.self_review_overall_rating === 'number' && (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: "600", color: "info.main" }}>
                          Self-Rating: {kra.self_review_overall_rating.toFixed(2)}/5.00
                        </Typography>
                        <Typography variant="caption" sx={{ color: "info.main", fontWeight: "600" }}>
                          {((kra.self_review_overall_rating / 5) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    )}
                    {kra.status === "pending" && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAcceptClick(kra)}
                        sx={{ mt: 1 }}
                      >
                        Accept KRA
                      </Button>
                    )}
                    {kra.status === "accepted" && (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => handleSelfReviewClick(kra)}
                        sx={{ mt: 1 }}
                      >
                        Self Review
                      </Button>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* KRA Sections */}
                <Box>
                  {kra.sections.map((section, sectionIndex) => (
                    <Accordion key={sectionIndex} elevation={0} sx={{ mb: 1 }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: "grey.50",
                          "&:hover": { backgroundColor: "grey.100" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            pr: 2,
                          }}
                        >
                          <Typography sx={{ fontWeight: "600" }}>
                            {sectionIndex + 1}. {section.section}
                          </Typography>
                          <Typography
                            sx={{ color: "primary.main", fontWeight: "600" }}
                          >
                            {section.weightage}%
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ pl: 2 }}>
                          {section.questions.map((kpi, kpiIndex) => (
                            <Box
                              key={kpiIndex}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                py: 1.5,
                                borderBottom:
                                  kpiIndex < section.questions.length - 1
                                    ? "1px solid"
                                    : "none",
                                borderColor: "grey.200",
                              }}
                            >
                              <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                                • {kpi.question}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "success.main",
                                  fontWeight: "600",
                                  ml: 2,
                                  minWidth: "50px",
                                  textAlign: "right",
                                }}
                              >
                                {kpi.weightage}%
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>

                {/* Total Weightage */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "primary.lighter",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "600" }}>
                    Total Weightage:{" "}
                    <span style={{ color: "green" }}>
                      {kra.sections.reduce((sum, s) => sum + s.weightage, 0)}%
                    </span>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Accept Confirmation Dialog */}
      <Dialog
        open={acceptDialogOpen}
        onClose={() => !accepting && setAcceptDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Accept KRA</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            By accepting this KRA, you acknowledge that you have reviewed and understood
            all the Key Responsibility Areas and Key Performance Indicators assigned to you.
          </Alert>
          {selectedKRA && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>KRA:</strong> {selectedKRA.form_name}
              </Typography>
              <Typography variant="body2" color="black">
                <strong>Total Sections:</strong> {selectedKRA.sections.length}
              </Typography>
              <Typography variant="body2" color="black">
                <strong>Assigned:</strong> {formatDate(selectedKRA.assigned_date)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)} disabled={accepting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAcceptKRA}
            disabled={accepting}
            startIcon={accepting ? <CircularProgress size={20} /> : null}
          >
            {accepting ? "Accepting..." : "Accept KRA"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Self-Review Dialog */}
      <Dialog
        open={selfReviewDialogOpen}
        onClose={() => !submittingSelfReview && setSelfReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Self Review
            </Typography>
            <Typography variant="body2" color="black">
              {selectedKRA?.form_name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedKRA && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please rate your performance for each KPI. The overall rating will be calculated automatically based on the weightages.
              </Alert>

              {/* KRA Sections */}
              {selectedKRA.sections.map((section, sectionIndex) => (
                <Accordion key={sectionIndex} defaultExpanded elevation={0} sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: "grey.50" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: "600" }}>
                        {sectionIndex + 1}. {section.section}
                      </Typography>
                      <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                        {section.weightage}%
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {section.questions.map((kpi, questionIndex) => (
                        <Box key={questionIndex}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                              {kpi.question}
                            </Typography>
                            <Chip
                              label={`${kpi.weightage}%`}
                              size="small"
                              color="primary"
                              sx={{ ml: 2 }}
                            />
                          </Box>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                              Your Rating: *
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <RatingInput
                                ratingType={kpi.rating_type}
                                value={kpi.self_rating}
                                onChange={(value) => handleSelfRatingChange(sectionIndex, questionIndex, value)}
                              />
                            </Box>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: "600", color: "info.main", mb: 0.5 }}>
                                {kpi.self_rating !== null && kpi.self_rating !== undefined ? `${formatRatingLabel(kpi.self_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : 'Not Rated'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "info.main", fontWeight: "600" }}>
                                {kpi.self_rating && kpi.self_rating > 0 ? `${((kpi.self_rating / 5) * 100).toFixed(1)}%` : '0.0%'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* KPI-specific Comments */}
                          <Box sx={{ mt: 2 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              required
                              label="Your Comments for this KPI"
                              placeholder="Add specific comments about your performance on this KPI..."
                              value={kpi.self_comments || ""}
                              onChange={(e) => handleKPICommentChange(sectionIndex, questionIndex, e.target.value)}
                              variant="outlined"
                              error={showKPICommentsError && (!kpi.self_comments || kpi.self_comments.trim() === "")}
                              helperText={showKPICommentsError && (!kpi.self_comments || kpi.self_comments.trim() === "") ? "Comment is required" : ""}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'background.paper',
                                },
                              }}
                            />
                          </Box>
                          
                          {questionIndex < section.questions.length - 1 && (
                            <Divider sx={{ mt: 3 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Divider sx={{ my: 3 }} />

              {/* Overall Comments */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Overall Comments *</Typography>
                <SkillDocumentCommentBox
                  value={selectedKRA.self_review_overall_comments || ""}
                  onChange={(val) => handleOverallCommentsChange(val)}
                  placeholder="Share your overall thoughts, key achievements, challenges faced, or any additional context about your performance this quarter..."
                  disabled={submittingSelfReview || savingDraft}
                  compact
                />
                {showCommentsError && isRichTextEmpty(selectedKRA.self_review_overall_comments) && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    Overall comments are required
                  </Typography>
                )}
              </Box>

              {/* Overall Rating Preview */}
              <Box sx={{ p: 2, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1 }}>
                  Overall Self-Rating (Auto-Calculated)
                </Typography>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                    {calculateSelfReviewOverallRating().toFixed(2)}/5.00
                  </Typography>
                  <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                    {((calculateSelfReviewOverallRating() / 5) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelfReviewDialogOpen(false)} disabled={submittingSelfReview || savingDraft}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={submittingSelfReview || savingDraft}
            startIcon={savingDraft ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {savingDraft ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitSelfReview}
            disabled={submittingSelfReview || savingDraft}
            startIcon={submittingSelfReview ? <CircularProgress size={20} /> : null}
          >
            {submittingSelfReview ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmployeeKRAView;
